import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { Label } from '../Label';
import styles from './Field.module.css';

/**
 * Field family — shadcn-style accessible form-field composition, reimplemented
 * in Athena's idiom (JSX + CSS Modules + 2.0 `--field-*` tokens). Pure layout +
 * state: `data-invalid` / `data-disabled` on <Field> cascade to the label /
 * description / error styling via CSS. Preview-only for now (globals.css isn't
 * imported into the app).
 *
 * Composition:
 *   <FieldSet> <FieldLegend/> <FieldDescription/>
 *     <FieldGroup>
 *       <Field [orientation]>
 *         <FieldLabel htmlFor/> <control/> <FieldDescription/> <FieldError/>
 *       </Field>
 *       <FieldSeparator/>
 *     </FieldGroup>
 *   </FieldSet>
 */

// Field — core wrapper for a single field. `orientation` lays the label/control
// vertically (default), side-by-side, or responsively. Pass `data-invalid` /
// `data-disabled` to switch the whole block into those states.
const Field = ({ orientation = 'vertical', className = '', children, ...props }) => (
  <div
    role="group"
    data-orientation={orientation}
    className={`${styles.field} ${styles[orientation] || ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

// FieldContent — flex column grouping label + description beside a control
// (horizontal / checkbox rows). Omit when there's no description.
const FieldContent = ({ className = '', children, ...props }) => (
  <div className={`${styles.content} ${className}`} {...props}>
    {children}
  </div>
);

// FieldLabel — label for a control; renders the Label primitive. `asChild`
// wraps a nested Field (choice-card pattern).
const FieldLabel = ({ className = '', children, ...props }) => (
  <Label className={`${styles.label} ${className}`} {...props}>
    {children}
  </Label>
);

// FieldTitle — label-styled title used inside FieldContent (no htmlFor).
const FieldTitle = ({ className = '', children, ...props }) => (
  <span className={`${styles.title} ${className}`} {...props}>
    {children}
  </span>
);

// FieldDescription — muted helper text.
const FieldDescription = ({ className = '', children, ...props }) => (
  <p className={`${styles.description} ${className}`} {...props}>
    {children}
  </p>
);

// FieldError — accessible error container; renders `children`, or maps an
// `errors` array ([{message}]) to a list. (Standard-Schema `issues` parsing
// is deferred — pass resolved messages via `errors` for now.)
const FieldError = ({ errors, className = '', children, ...props }) => {
  const messages = Array.isArray(errors)
    ? errors.filter(Boolean).map((e) => e?.message).filter(Boolean)
    : null;
  if (!children && (!messages || messages.length === 0)) return null;
  return (
    <div role="alert" className={`${styles.error} ${className}`} {...props}>
      {children}
      {messages && messages.length === 1 && <span>{messages[0]}</span>}
      {messages && messages.length > 1 && (
        <ul className={styles.errorList}>
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

// FieldGroup — vertical stack of related Fields.
const FieldGroup = ({ className = '', children, ...props }) => (
  <div role="group" className={`${styles.group} ${className}`} {...props}>
    {children}
  </div>
);

// FieldSet — semantic <fieldset> with spacing presets.
const FieldSet = ({ className = '', children, ...props }) => (
  <fieldset className={`${styles.set} ${className}`} {...props}>
    {children}
  </fieldset>
);

// FieldLegend — <legend> for a FieldSet; `label` variant uses label sizing.
const FieldLegend = ({ variant = 'legend', className = '', children, ...props }) => (
  <legend
    className={`${styles.legend} ${variant === 'label' ? styles.legendLabel : ''} ${className}`}
    {...props}
  >
    {children}
  </legend>
);

// FieldSeparator — divider between sections; optional centered inline content.
const FieldSeparator = ({ className = '', children, ...props }) => (
  <div
    className={`${styles.separator} ${children ? styles.separatorWithText : ''} ${className}`}
    role="separator"
    {...props}
  >
    {children && <span className={styles.separatorText}>{children}</span>}
  </div>
);

Field.propTypes = { orientation: PropTypes.string, className: PropTypes.string, children: PropTypes.node };
FieldContent.propTypes = { className: PropTypes.string, children: PropTypes.node };
FieldLabel.propTypes = { className: PropTypes.string, children: PropTypes.node };
FieldTitle.propTypes = { className: PropTypes.string, children: PropTypes.node };
FieldDescription.propTypes = { className: PropTypes.string, children: PropTypes.node };
FieldError.propTypes = { errors: PropTypes.array, className: PropTypes.string, children: PropTypes.node };
FieldGroup.propTypes = { className: PropTypes.string, children: PropTypes.node };
FieldSet.propTypes = { className: PropTypes.string, children: PropTypes.node };
FieldLegend.propTypes = { variant: PropTypes.string, className: PropTypes.string, children: PropTypes.node };
FieldSeparator.propTypes = { className: PropTypes.string, children: PropTypes.node };

export {
  Field,
  FieldContent,
  FieldLabel,
  FieldTitle,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldSet,
  FieldLegend,
  FieldSeparator,
};
